import {useCallback, useMemo, useState} from 'react';
import {Box, Chip, Tooltip} from '@mui/material';
import StarOutlined from '@mui/icons-material/StarOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
    getWpStatusTooltip,
    resolveScheduleChipProps,
    shouldLoadScheduleChipTooltip,
    WP_STATUS_ISSUE_TOOLTIP_CONTENT_SX,
    WP_STATUS_ISSUE_TOOLTIP_ICON_SX,
    WP_STATUS_ISSUE_TOOLTIP_SLOT_PROPS,
    WP_STATUS_ISSUE_TOOLTIP_TEXT_SX,
    type ScheduleChipItem,
} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/wpStatusUtils';

/** Schedule codes that show a must-fill star on the chip. */
export const MUST_FILL_SCHEDULE_CODES = new Set<string>([
    'BS-NCA-PPE',
    'BS-EQ-IC',
    'PL-TURN-SE',
    'PL-TURN-SI',
    'PL-TURN-RI',
    'PL-AE-DEP',
]);

/** True when code is in the must-fill set, or schedule label contains "X". */
export function isMustFillScheduleCode(code?: string | null, scheduleLabel?: string | null): boolean {
    const c = String(code || '').trim();
    if (c !== '' && MUST_FILL_SCHEDULE_CODES.has(c)) {
        return true;
    }
    return String(scheduleLabel || '').includes('X');
}

/** Schedule chip content height (= schedule-column row content); star uses 70% of this. */
const SCHEDULE_CHIP_HEIGHT_PX = 18;
const MUST_FILL_STAR_SIZE_PX = SCHEDULE_CHIP_HEIGHT_PX * 0.7;

export type ScheduleChipProps = {
    mode?: 'fs' | 'adjustment' | string;
    item: ScheduleChipItem;
    label: string;
    onClick?: () => void;
    /** Visual must-fill mark (gold star); no interaction. */
    mustFill?: boolean;
};

export function ScheduleChip({mode, item, label, onClick, mustFill = false}: ScheduleChipProps) {
    const [tooltipLoaded, setTooltipLoaded] = useState(false);
    const chipStyleProps = resolveScheduleChipProps(mode, item);
    const needsTooltip = shouldLoadScheduleChipTooltip(mode, item.wpStatus, item.outstanding);

    const handleMouseEnter = useCallback(() => {
        if (needsTooltip) {
            setTooltipLoaded(true);
        }
    }, [needsTooltip]);

    const chip = (
        <Box
            sx={{
                position: 'relative',
                display: 'inline-flex',
                height: SCHEDULE_CHIP_HEIGHT_PX,
                alignItems: 'center',
                overflow: 'visible',
                verticalAlign: 'middle',
            }}
        >
            <Chip
                size="small"
                label={label}
                clickable
                onClick={onClick}
                sx={{height: SCHEDULE_CHIP_HEIGHT_PX, '& .MuiChip-label': {px: 1}}}
                {...chipStyleProps}
                onMouseEnter={handleMouseEnter}
            />
            {mustFill ? (
                <StarOutlined
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: MUST_FILL_STAR_SIZE_PX,
                        height: MUST_FILL_STAR_SIZE_PX,
                        fontSize: MUST_FILL_STAR_SIZE_PX,
                        // Light orange — distinct from Processing yellow (#FFEB3B) and Issue red.
                        color: '#FFB74D',
                        filter: 'drop-shadow(0 0 0.6px #BF360C)',
                        pointerEvents: 'none',
                        lineHeight: 1,
                        // Sit on chip top-right corner (half over chip, half outside).
                        transform: 'translate(35%, -35%)',
                    }}
                />
            ) : null}
        </Box>
    );

    const tooltipText = getWpStatusTooltip(
        item.wpStatus,
        item.wpIssueRemark,
        item.outstanding,
        item.outstandingRemark,
    ) ?? '';

    const tooltipTitle = useMemo(() => {
        if (!tooltipLoaded || !tooltipText) {
            return '';
        }
        return (
            <Box component="span" sx={WP_STATUS_ISSUE_TOOLTIP_CONTENT_SX}>
                <WarningAmberIcon sx={WP_STATUS_ISSUE_TOOLTIP_ICON_SX} />
                <Box component="span" sx={WP_STATUS_ISSUE_TOOLTIP_TEXT_SX}>
                    {tooltipText}
                </Box>
            </Box>
        );
    }, [tooltipLoaded, tooltipText]);

    if (!needsTooltip) {
        return chip;
    }

    return (
        <Tooltip
            title={tooltipTitle}
            enterDelay={50}
            enterNextDelay={50}
            slotProps={WP_STATUS_ISSUE_TOOLTIP_SLOT_PROPS}
        >
            {chip}
        </Tooltip>
    );
}
